import React, { useState, useEffect, useMemo } from "react";
import { useHistory } from "react-router-dom";
import config from "@config";
import utilityFunctions from "@store/utilityFunctions";
import errorFunctions from "@store/errorFunctions";
import { useToast } from "@components/Toast/ToastContext";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Modal from "react-modal";
import BrandSettingAdd from "./BrandSettingAdd";
import {
  MdClose,
  MdBusiness,
  MdSearch,
  MdWarning,
  MdImage,
  MdEdit,
  MdDelete,
  MdArrowDropUp,
  MdArrowDropDown,
  MdAdd,
  MdSettings,
} from "react-icons/md";
import "../common.css";
import LoadingPage from "@components/Loading/Loading";
import apiEndpoints from "@store/apiEndpoints";

const COUNTRY_COLORS = {
  AU: { background: "#dbeafe", color: "#1e40af" },
  NZ: { background: "#fee2e2", color: "#991b1b" },
};

const BrandSettings = ({ userInfo, protectedFetch }) => {
  const { showToast } = useToast();
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [brands, setBrands] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [showBrandAdd, setShowBrandAdd] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [brandToDelete, setBrandToDelete] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("");
  const [sortDir, setSortDir] = useState("asc");
  const [previewData, setPreviewData] = useState(null);

  useEffect(() => {
    retrieveBrands();
  }, []);

  const retrieveBrands = () => {
    setLoading(true);
    
    protectedFetch(
      apiEndpoints.brand.list(true),
      "GET",
      null,
      errorFunctions.checkResponseThenJson,
    )
      .then((res) => {
        setBrands(res);
      })
      .catch(() => {
        showToast("Failed to load brands", "error");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const filteredBrands = useMemo(() => {
    let result = brands;
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        (brand) =>
          (brand.name && brand.name.toLowerCase().includes(term)) ||
          (brand.country && brand.country.toLowerCase().includes(term)) ||
          (brand.tag && brand.tag.toLowerCase().includes(term)),
      );
    }
    if (sortBy) {
      result = [...result].sort((a, b) => {
        const aVal = (a[sortBy] || "").toLowerCase();
        const bVal = (b[sortBy] || "").toLowerCase();
        if (aVal < bVal) return sortDir === "a sc" ? -1 : 1;
        if (aVal > bVal) return sortDir === "asc" ? 1 : -1;
        return 0;
      });
    }
    return result;
  }, [brands, searchTerm, sortBy, sortDir]);

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(column);
      setSortDir("asc");
    }
  };

  const renderSortIcon = (column) => {
    if (sortBy !== column) return null;
    return sortDir === "asc" ? (
      <MdArrowDropUp size={18} style={{ verticalAlign: "middle" }} />
    ) : (
      <MdArrowDropDown size={18} style={{ verticalAlign: "middle" }} />
    );
  };

  const deleteBrand = async () => {
    setDeleting(true);
    const token = utilityFunctions.getAccessToken();
    const options = {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + token,
      },
    };

    const url = config.backendServer + "brand/" + brandToDelete.id + "/";
    fetch(url, options)
      .then(async (resp) => {
        if (resp.status === 406) {
          const errorMsg = await resp.json();
          showToast(errorMsg?.error || errorMsg?.message, "error");
        } else if (resp.status === 204) {
          showToast("Brand deleted successfully", "success");
          retrieveBrands();
        } else {
          const errorMsg = await resp.json().catch(() => ({}));
          showToast(errorMsg?.error || errorMsg.message || "Failed to delete brand", "error");
        }
        setShowDeleteModal(false);
        setBrandToDelete(null);
      })
      .catch(() => {
        showToast("Failed to delete brand", "error");
      })
      .finally(() => {
        setDeleting(false);
      });
  };

  const handleEdit = (brand) => {
    setSelectedBrand(brand);
    setShowBrandAdd(true);
  };

  const handleDelete = (brand) => {
    setBrandToDelete(brand);
    setShowDeleteModal(true);
  };

  const handleAddNew = () => {
    setSelectedBrand(null);
    setShowBrandAdd(true);
  };

  const closeAddModal = () => {
    setShowBrandAdd(false);
    setSelectedBrand(null);
  };

  const handleBrandCreated = (brandId) => {
    setShowBrandAdd(false);
    setSelectedBrand(null);
    history.push(`/brand-setup/${brandId}`);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setBrandToDelete(null);
  };

  const handleImageError = (e) => {
    e.target.style.display = "none";
    const parent = e.target.parentNode;
    if (parent) {
      const fallback = parent.querySelector(".img-fallback");
      if (fallback) fallback.style.display = "inline-flex";
    }
  };

  const renderImageCell = (src, label) => (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      {src ? (
        <img
          src={src}
          alt={label}
          title={`Click to preview ${label}`}
          onError={handleImageError}
          onClick={() => setPreviewData({ src, label })}
          className="brand-thumb-img"
          style={{
            width: 44,
            height: 44,
            objectFit: "contain",
            borderRadius: "8px",
            border: "2px solid #e5e7eb",
            cursor: "pointer",
            padding: 2,
            background: "#fff",
            transition:
              "transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "scale(1.15)";
            e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.15)";
            e.currentTarget.style.borderColor = "#4f46e5";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "scale(1)";
            e.currentTarget.style.boxShadow = "none";
            e.currentTarget.style.borderColor = "#e5e7eb";
          }}
        />
      ) : (
        <span
          className="img-fallback"
          title={`No ${label}`}
          style={{
            display: "inline-flex",
            width: 44,
            height: 44,
            borderRadius: "8px",
            background: "#f9fafb",
            border: "2px dashed #e5e7eb",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <MdImage size={18} color="#d1d5db" />
        </span>
      )}
    </div>
  );

  const renderCountryBadge = (country) => {
    const colors = COUNTRY_COLORS[country] || {
      background: "#f1f5f9",
      color: "#475569",
    };
    return (
      <span
        style={{
          display: "inline-block",
          padding: "2px 10px",
          borderRadius: "12px",
          fontSize: "0.8rem",
          fontWeight: 600,
          background: colors.background,
          color: colors.color,
        }}
      >
        {country}
      </span>
    );
  };

  if (loading) {
    return <LoadingPage />;
  }

  const isAdmin = userInfo?.user?.role === "Admin";

  const isFiltered = searchTerm.trim() !== "";
  const sortedAndFiltered = filteredBrands;

  return (
    <div className="page">
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 20,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div
            style={{
              width: 42,
              height: 42,
              borderRadius: 10,
              background: "#eef2ff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <MdBusiness style={{ fontSize: "1.3rem", color: "#4f46e5" }} />
          </div>
          <div>
            <h5
              style={{
                margin: 0,
                fontSize: "1.15rem",
                fontWeight: 700,
                color: "#1f2937",
              }}
            >
              Brand Settings
            </h5>
            {brands.length > 0 && (
              <p
                style={{
                  margin: "2px 0 0",
                  fontSize: "0.8rem",
                  color: "#6b7280",
                }}
              >
                {brands.length} brand{brands.length !== 1 ? "s" : ""} configured
              </p>
            )}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            className="btn btn-outline-primary btn-sm"
            onClick={() => history.push("/brand-setup/new")}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              padding: "7px 16px",
              fontWeight: 500,
            }}
          >
            <MdSettings size={14} /> Setup New
          </button>
        </div>
      </div>

      {brands.length > 0 && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            marginBottom: 16,
          }}
        >
          <div className="input-group input-group-sm" style={{ maxWidth: 360 }}>
            <span
              className="input-group-text"
              style={{
                background: "white",
                borderRight: "none",
                borderRadius: "8px 0 0 8px",
              }}
            >
              <MdSearch size={16} color="#9ca3af" />
            </span>
            <input
              type="text"
              className="form-control"
              placeholder="Search by name, country, tag..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                borderLeft: "none",
                borderRadius: searchTerm ? 0 : "0 8px 8px 0",
              }}
            />
            {searchTerm && (
              <span
                className="input-group-text"
                onClick={() => setSearchTerm("")}
                title="Clear search"
                style={{
                  background: "white",
                  borderLeft: "none",
                  borderRadius: "0 8px 8px 0",
                  cursor: "pointer",
                  padding: "0 10px",
                }}
              >
                <MdClose size={16} color="#6b7280" />
              </span>
            )}
          </div>
          {isFiltered && (
            <span style={{ fontSize: "0.8rem", color: "#6b7280" }}>
              Showing {sortedAndFiltered.length} of {brands.length} brand
              {brands.length !== 1 ? "s" : ""}
            </span>
          )}
        </div>
      )}

      <TableContainer component={Paper}>
        <Table
          aria-labelledby="tableTitle"
          size={"small"}
          aria-label="enhanced table"
        >
          <TableHead>
            <TableRow style={{ background: "#f9fafb" }}>
              <TableCell
                style={{
                  width: 200,
                  fontWeight: 700,
                  cursor: "pointer",
                  userSelect: "none",
                }}
                onClick={() => handleSort("country")}
              >
                Country {renderSortIcon("country")}
              </TableCell>
              <TableCell
                style={{
                  width: 400,
                  fontWeight: 700,
                  cursor: "pointer",
                  userSelect: "none",
                }}
                onClick={() => handleSort("name")}
              >
                Name {renderSortIcon("name")}
              </TableCell>
              <TableCell style={{ width: 150, fontWeight: 700 }}>Tag</TableCell>
              <TableCell
                style={{ width: 80, fontWeight: 700, textAlign: "center" }}
              >
                Logo
              </TableCell>
              <TableCell
                style={{ width: 80, fontWeight: 700, textAlign: "center" }}
              >
                Image
              </TableCell>
              <TableCell
                style={{ width: 100, fontWeight: 700, textAlign: "center" }}
              >
                Status
              </TableCell>
              <TableCell style={{ fontWeight: 700, textAlign: "right" }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {brands.length > 0 ? (
              filteredBrands.length > 0 ? (
                filteredBrands.map((brand) => (
                  <TableRow
                    key={brand.id}
                    style={{
                      transition: "background 0.15s ease",
                    }}
                    sx={{
                      "&:hover": { background: "#f5f7ff" },
                    }}
                  >
                    <TableCell>{renderCountryBadge(brand.country)}</TableCell>
                    <TableCell
                      style={{ fontWeight: 500, color: "#1f2937" }}
                      className="text-break"
                    >
                      {brand.name}
                    </TableCell>
                    <TableCell className="text-break">
                      {brand.tag ? (
                        <code
                          style={{
                            background: "#f3f4f6",
                            padding: "2px 8px",
                            borderRadius: "4px",
                            fontSize: "0.8rem",
                            color: "#4b5563",
                          }}
                        >
                          {brand.tag}
                        </code>
                      ) : (
                        <span style={{ color: "#d1d5db", fontSize: "0.8rem" }}>
                          —
                        </span>
                      )}
                    </TableCell>
                    <TableCell style={{ textAlign: "center" }}>
                      {renderImageCell(brand.logo, "Logo")}
                    </TableCell>
                    <TableCell style={{ textAlign: "center" }}>
                      {renderImageCell(brand.image, "Image")}
                    </TableCell>
                    <TableCell style={{ textAlign: "center" }}>
                      {brand.is_active ? (
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 5,
                            padding: "2px 10px",
                            borderRadius: "12px",
                            fontSize: "0.78rem",
                            fontWeight: 600,
                            background: "#d1fae5",
                            color: "#065f46",
                          }}
                        >
                          Active
                        </span>
                      ) : (
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 5,
                            padding: "2px 10px",
                            borderRadius: "12px",
                            fontSize: "0.78rem",
                            fontWeight: 600,
                            background: "#fef3c7",
                            color: "#d97706",
                          }}
                        >
                          Inactive
                          <span
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              width: 18,
                              height: 18,
                              borderRadius: "50%",
                              background: "#fde68a",
                              color: "#d97706",
                              fontSize: "0.7rem",
                              fontWeight: 700,
                            }}
                          >
                            !
                          </span>
                        </span>
                      )}
                    </TableCell>
                    <TableCell style={{ textAlign: "right" }}>
                      <div
                        style={{
                          display: "flex",
                          gap: 6,
                          justifyContent: "flex-end",
                        }}
                      >
                        <button
                          className="btn btn-outline-secondary btn-sm"
                          onClick={() => handleEdit(brand)}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 3,
                            borderRadius: 6,
                            padding: "4px 10px",
                            fontSize: "0.8rem",
                          }}
                        >
                          <MdEdit size={13} /> Edit
                        </button>
                        <button
                          className="btn btn-outline-primary btn-sm"
                          onClick={() => history.push(`/brand-setup/${brand.id}`)}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 3,
                            borderRadius: 6,
                            padding: "4px 10px",
                            fontSize: "0.8rem",
                          }}
                        >
                          <MdSettings size={12} /> Setup
                        </button>
                        <button
                          disabled={!isAdmin}
                          className="btn btn-outline-danger btn-sm"
                          onClick={() => handleDelete(brand)}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 3,
                            borderRadius: 6,
                            padding: "4px 10px",
                            fontSize: "0.8rem",
                          }}
                        >
                          <MdDelete size={13} /> Delete
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={7}
                    style={{ textAlign: "center", padding: "40px 16px" }}
                  >
                    <div
                      style={{
                        width: 56,
                        height: 56,
                        borderRadius: "50%",
                        background: "#f3f4f6",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        marginBottom: 12,
                      }}
                    >
                      <MdSearch size={28} color="#9ca3af" />
                    </div>
                    <p
                      style={{
                        color: "#4b5563",
                        fontWeight: 500,
                        marginBottom: 4,
                      }}
                    >
                      No brands matching &ldquo;{searchTerm}&rdquo;
                    </p>
                    <p
                      style={{
                        color: "#9ca3af",
                        fontSize: "0.85rem",
                        marginBottom: 12,
                      }}
                    >
                      Try adjusting your search terms
                    </p>
                    <button
                      className="btn btn-outline-secondary btn-sm"
                      onClick={() => setSearchTerm("")}
                      style={{ borderRadius: 6 }}
                    >
                      Clear search
                    </button>
                  </TableCell>
                </TableRow>
              )
            ) : (
              <TableRow>
                <TableCell
                  colSpan={7}
                  style={{ textAlign: "center", padding: "56px 16px" }}
                >
                  <div
                    style={{
                      width: 64,
                      height: 64,
                      borderRadius: "50%",
                      background: "#f3f4f6",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginBottom: 16,
                    }}
                  >
                    <MdBusiness size={32} color="#9ca3af" />
                  </div>
                  <h6
                    style={{
                      color: "#374151",
                      fontWeight: 600,
                      marginBottom: 4,
                    }}
                  >
                    No brands configured yet
                  </h6>
                  <p
                    style={{
                      color: "#9ca3af",
                      fontSize: "0.85rem",
                      marginBottom: 16,
                    }}
                  >
                    {isAdmin
                      ? "Get started by adding your first brand"
                      : "Contact an admin to configure brands"}
                  </p>
                  {isAdmin && (
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={handleAddNew}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                        borderRadius: 8,
                        padding: "7px 18px",
                      }}
                    >
                      <MdAdd size={16} /> Add Brand
                    </button>
                  )}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Modal
        isOpen={!!previewData}
        className={"input-modal-mid"}
        onRequestClose={() => setPreviewData(null)}
        style={{
          overlay: {
            background: "rgba(0,0,0,0.6)",
            zIndex: 1000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          },
        }}
      >
        <div
          style={{
            background: "#fff",
            borderRadius: 12,
            overflow: "hidden",
            maxWidth: "90vw",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "12px 20px",
              borderBottom: "1px solid #e5e7eb",
              background: "#f9fafb",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 8,
                  background: "#eef2ff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <MdImage size={16} color="#4f46e5" />
              </div>
              <span
                style={{
                  fontWeight: 600,
                  fontSize: "0.9rem",
                  color: "#1f2937",
                }}
              >
                {previewData?.label} Preview
              </span>
            </div>
            <button
              onClick={() => setPreviewData(null)}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: 4,
                borderRadius: 6,
                display: "flex",
                alignItems: "center",
                color: "#6b7280",
              }}
            >
              <MdClose size={20} />
            </button>
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: 200,
              padding: 32,
              background: "#f3f4f6",
            }}
          >
            <img
              src={previewData?.src}
              alt={previewData?.label || "Preview"}
              style={{
                maxWidth: "100%",
                maxHeight: "65vh",
                objectFit: "contain",
                borderRadius: 6,
              }}
            />
          </div>
        </div>
      </Modal>

      <Modal isOpen={showBrandAdd} className={"input-modal-mid"}>
        <button onClick={closeAddModal} className={"info-modal-close"}>
          <MdClose />
        </button>
        <BrandSettingAdd
          closeModal={closeAddModal}
          retrieveBrands={retrieveBrands}
          userInfo={userInfo}
          selectedBrand={selectedBrand}
          protectedFetch={protectedFetch}
          onBrandCreated={handleBrandCreated}
        />
      </Modal>

      <Modal
        isOpen={showDeleteModal}
        contentLabel={"Delete Brand"}
        className={"confirm-modal"}
      >
        <div style={{ padding: "20px" }}>
          <div
            style={{
              width: 48,
              height: 48,
              borderRadius: "50%",
              background: "#fef3c7",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 12px",
            }}
          >
            <MdWarning size={24} style={{ color: "#d97706" }} />
          </div>
          <h6 style={{ fontWeight: 700, marginBottom: 4, color: "#1f2937" }}>
            Delete Brand
          </h6>
          <p style={{ color: "#6b7280", fontSize: "0.85rem", marginBottom: 4 }}>
            Are you sure you want to delete{" "}
            <strong>{brandToDelete?.name}</strong>?
          </p>
          <p style={{ color: "#9ca3af", fontSize: "0.8rem", marginBottom: 20 }}>
            This action cannot be undone.
          </p>
          <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
            <button
              className="btn btn-outline-secondary btn-sm"
              onClick={closeDeleteModal}
              disabled={deleting}
            >
              Cancel
            </button>
            <button
              className="btn btn-danger btn-sm"
              onClick={deleteBrand}
              disabled={deleting}
            >
              {deleting ? "Deleting..." : "Delete"}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default BrandSettings;
